import { useEffect, useMemo, useState } from 'react'
import { Box } from '@astro-ui/components/Box'
import { Select } from '@astro-ui/components/Select'
import { Typography } from '@astro-ui/components/Typography'

import SelectedChipsValue from 'features/MultiCategory/_component/SelectedChipsValue'

import useLocationHubs, { HubOptionType } from './useLocationHubs'

/**
 * Prepended "all hubs" sentinel. Selecting it clears the individual hubs, and
 * the payload sends it as `location_ids: [0]` — an empty list means no hub.
 */
export const ALL_HUB_OPTION: HubOptionType = { name: 'Semua Hub', value: 'all' }

export const ALL_HUB_VALUE = ALL_HUB_OPTION.value

export type LocationHubsPropsType = {
  /** Selected hub ids (controlled). */
  value: string[]
  onChange: (value: string[]) => void
  /** Field label above the dropdown. */
  label?: string
  placeholder?: string
  /** Override the API-fetched hub options. Omit to fetch from the API. */
  hubOptions?: HubOptionType[]
  /** Prepend the "Semua Hub" (all) option. Defaults to true. */
  withAllOption?: boolean
}

const LocationHubs = ({
  value,
  onChange,
  label = 'Pilih Hub',
  placeholder = 'Pilih Hub',
  hubOptions,
  withAllOption = true,
}: LocationHubsPropsType) => {
  // Fetch options from the API unless the consumer supplies its own list.
  const {
    options: fetchedOptions,
    isLoading,
    searchValue,
    handleSearch,
    resolveHubById,
  } = useLocationHubs({
    enabled: !hubOptions,
  })
  const baseOptions = hubOptions ?? fetchedOptions

  const options = useMemo(
    () => (withAllOption ? [ALL_HUB_OPTION, ...baseOptions] : baseOptions),
    [withAllOption, baseOptions],
  )

  /**
   * Holds the picked hubs so their names survive a server-side search that
   * filters them out of `options`, and carries stored ids resolved by the API.
   */
  const [resolvedOptions, setResolvedOptions] = useState<HubOptionType[]>([])

  const findOption = (hubId: string) =>
    options.find((option) => option.value === hubId) ||
    resolvedOptions.find((option) => option.value === hubId)

  const selectedOptions = useMemo(
    () =>
      value
        .map((hubId) => findOption(hubId))
        .filter((option): option is HubOptionType => Boolean(option)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value, options, resolvedOptions],
  )

  /* Ids the paged list does not carry are looked up one by one. */
  const unresolvedIds = value.filter((hubId) => !findOption(hubId))

  useEffect(() => {
    let isActive = true

    if (unresolvedIds.length && !hubOptions) {
      Promise.all(unresolvedIds.map((hubId) => resolveHubById(hubId))).then((results) => {
        const found = results.filter((option): option is HubOptionType => Boolean(option))

        if (isActive && found.length) {
          setResolvedOptions((prev) => [
            ...prev,
            ...found.filter((option) => !prev.some((item) => item.value === option.value)),
          ])
        }
      })
    }

    return () => {
      isActive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unresolvedIds.join(','), hubOptions])

  // API mode searches server-side; overridden options fall back to the
  // Select's built-in client-side filter.
  const searchProps = hubOptions
    ? { withSearch: true, searchPlaceholder: 'Cari Hub' }
    : { withSearch: true, searchPlaceholder: 'Cari Hub', searchValue, onSearch: handleSearch }

  return (
    <Box width="100%">
      <Typography fontSize="14px" fontWeight="bold" marginBottom="8px">
        {label}
      </Typography>

      <Select
        fullWidth
        multiple
        /* Select All would tick "Semua Hub" alongside the individual hubs. */
        multipleProps={{ hideSelectAll: true }}
        isLoading={isLoading}
        placeholder={placeholder}
        options={options}
        /* Select reads `displayKey` off the values, so bare ids render as ids. */
        value={selectedOptions}
        /* One row of plain chips with a `+N` counter instead of wrapping deletable chips. */
        CustomRenderSelectedValue={() => (
          <SelectedChipsValue labels={selectedOptions.map((option) => option.name)} />
        )}
        onChange={(picked: HubOptionType[]) => {
          const pickedOptions = picked || []
          const hasAll = pickedOptions.some((option) => option.value === ALL_HUB_VALUE)
          /* "Semua Hub" and individual hubs are mutually exclusive. */
          const nextOptions =
            hasAll && !value.includes(ALL_HUB_VALUE)
              ? [ALL_HUB_OPTION]
              : pickedOptions.filter((option) => option.value !== ALL_HUB_VALUE)

          /* Keep the names around once a search narrows the option list. */
          setResolvedOptions((prev) => [
            ...prev,
            ...nextOptions.filter((option) => !prev.some((item) => item.value === option.value)),
          ])
          onChange(nextOptions.map((option) => option.value))
        }}
        required
        {...searchProps}
      />
    </Box>
  )
}

export default LocationHubs
